// Plan 24a, Task 2.4a — Lobby Host shell (Screen 4)
// Plan 24a, Task 2.4b — Lobby Host settings (rounds stepper + map name + START GAME)
// Plan 24a, Task 2.4c — Lobby Host Special Rules (board-modifier toggles + Minigames Only branch)
//
// Panel (720px wide, padding: 'lg') with:
//   - Top: RoomCodeDisplay (placeholder '----' until room_created)
//   - Center (two-column row):
//     Left (~60%): 2-column × 4-row player slot grid (8 cells max)
//       Empty slot: "Add Bot" + "Add Local Player" ghost buttons
//       Filled slot: colored circle avatar + player name + optional HOST amber pill
//     Right (~40%, board_game mode only):
//       map preview + Special Rules fieldset + Rounds stepper + Dice read-only row
//       In minigames_only mode: replaced by "MINIGAMES ONLY MODE" label
//   - Bottom: START GAME button (enabled when ≥ 2 players)
//
// Design system references:
//   - docs/design/tokens.json — color.player.*, spacing.sm, color.brand.amber,
//     border-radius.pill, color.text.primary, color.text.secondary,
//     typography.family.headline, typography.size.caption
//   - docs/design/components.md — Panel §3, RoomCodeDisplay §15, Button §1,
//     NumberStepper §22, SettingsToggle §21

import type { ClientMessage, ServerMessage } from '@shared/protocol';
import { deserializeGameState } from '@shared/protocol';
import { DEFAULT_GAME_SETTINGS, isBetweenRoundMinigamesEnabled } from '@shared/game';
import { COLOR_ID_TO_HEX } from '@shared/game/types';
import type { CharacterCustomization } from '@shared/game/types';
import type { ColorId } from '../../renderer/character';
import { createPanel } from '../components/panel';
import { createRoomCodeDisplay } from '../components/room-code';
import { createButton } from '../components/button';
import { createNumberStepper } from '../components/number-stepper';
import { createSettingsToggle } from '../components/settings-toggle';
import type { PanelHandle } from '../components/panel';
import type { RoomCodeDisplayHandle } from '../components/room-code';
import type { UIComponentHandle } from '../components/types';
import type { ScreenBackTarget, ScreenHandle, ScreenMountContext } from './types';
import type { CharacterPreviewAdapter } from './character-preview-adapter';
import {
  createCharacterSelectScreen,
  type CharacterCustomizeCameraRequest,
} from './character-select';
import { buildWarmupSubscribeMessage, hasSeenWarmupTutorial } from '../../portal-entry';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface LobbyMountPayload {
  /** mode + roomType from Main Menu navigation */
  readonly mode: 'board_game' | 'minigames_only';
  readonly roomType: 'create_game' | 'quick_play';
  /** For Lobby Joiner only — code typed at Main Menu's Join Game subview */
  readonly code?: string;
  /** Player name (mirrored from localStorage at navigation time) */
  readonly playerName: string;
  /** Send a ClientMessage over the WS — caller-provided injection so screens
   *  remain decoupled from network transport and easy to mock in tests. */
  send(msg: ClientMessage): void;
  /** Subscribe to ServerMessages — returns an unsubscribe function. */
  subscribe(handler: (msg: ServerMessage) => void): () => void;
  /** Optional time source for tests; defaults to performance.now. */
  now?: () => number;
  /** Production character preview adapter — null if assets failed to load. */
  characterAdapter?: CharacterPreviewAdapter | null;
  /** Optional live 3D camera focus hook for inline character customization. */
  onCustomizeCamera?: (request: CharacterCustomizeCameraRequest) => void;
  /** True once every selectable cosmetic asset is available to Character Select. */
  areCharacterCosmeticsLoaded?: () => boolean;
  /** Background/interactive preload for every selectable cosmetic asset. */
  ensureCharacterCosmeticsLoaded?: () => Promise<void>;
  /** Restore path: player IDs this client owns before the first lobby state_sync. */
  initialOwnedPlayerIds?: readonly string[];
  /** Preferred customization from portal entry or prior character selection. */
  preferredCustomization?: CharacterCustomization;
  /** Restore path only: mount the lobby around an existing server-side slot. */
  skipInitialRequest?: boolean;
  restoredSession?: {
    playerId: string;
    hostId: string;
    roomCode: string;
  };
  /** Optional ESC/back target supplied by callers that mount lobby from an overlay. */
  escBackTarget?: ScreenBackTarget;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal types
// ─────────────────────────────────────────────────────────────────────────────

interface PlayerEntry {
  playerId: string;
  playerName: string;
  isBot: boolean;
  isLocal: boolean;
  isReady: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants (tokens from docs/design/tokens.json)
// ─────────────────────────────────────────────────────────────────────────────

const MAX_PLAYERS = 8;
const PANEL_WIDTH = '720px';
// No breakpoint tokens currently exist in docs/design/tokens.json.
const BREAKPOINT_WIDE = '1280px';
const BREAKPOINT_NARROW = '720px';

// color.player.* from tokens.json
const PLAYER_COLORS: readonly string[] = [
  '#E04040', // red
  '#4080D0', // blue
  '#40B040', // green
  '#E8D020', // yellow
  '#E08820', // orange
  '#9040C0', // purple
  '#E06090', // pink
  '#40B0C0', // cyan
];

// color.brand.amber = #E8A020 (HOST pill)
const COLOR_AMBER = '#E8A020';
const COLOR_SUCCESS = '#5CAD40';
// border-radius.pill = 9999px
const BORDER_RADIUS_PILL = '9999px';
// spacing.sm = 8px (slot gap)
const SLOT_GAP = '8px';
// color.text.primary = #FFFFFF
const COLOR_TEXT_PRIMARY = '#FFFFFF';
// color.text.secondary = #B0B8C8
const COLOR_TEXT_SECONDARY = '#B0B8C8';
// typography.family.headline
const FONT_HEADLINE =
  "var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)";

export function isTouchLobbyLayout(win: Window = window): boolean {
  const coarse = win.matchMedia?.('(pointer: coarse)').matches === true;
  const noHover = win.matchMedia?.('(hover: none)').matches === true;
  if (coarse || noHover) return true;
  if (win.matchMedia == null) {
    return (win.innerWidth ?? 1024) < 1024;
  }
  return false;
}

function injectLobbyResponsiveStyles(): void {
  const styleId = 'lobby-responsive-styles';
  if (document.getElementById(styleId)) return;

  const styleEl = document.createElement('style');
  styleEl.id = styleId;
  styleEl.textContent = `
    .screen-lobby-host,
    .screen-lobby-joiner {
      --lobby-screen-padding: 24px;
      --lobby-screen-gap: 24px;
      --lobby-panel-padding-y: 24px;
      --lobby-panel-padding-x: 28px;
      --lobby-panel-content-gap: 20px;
      --lobby-room-code-font-size: 48px;
      --lobby-slot-min-height: 100px;
      --lobby-slot-padding: 12px 8px;
      --lobby-empty-slot-padding: 8px;
      --lobby-slot-gap: 6px;
      --lobby-avatar-size: 40px;
      --lobby-settings-padding: 10px;
      --lobby-settings-gap: 8px;
      --layout-button-min-height: 44px;
      --layout-button-padding: 10px 18px;
      --typography-size-h1: var(--lobby-room-code-font-size);
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--lobby-screen-gap);
      width: 100%;
      height: var(--app-viewport-height, 100vh);
      padding: var(--lobby-screen-padding);
      box-sizing: border-box;
      overflow: hidden;
    }

    .screen-lobby-host--touch {
      display: block;
      position: relative;
      width: 100%;
      height: var(--app-viewport-height, 100vh);
      min-height: var(--app-viewport-height, 100vh);
      max-height: var(--app-viewport-height, 100vh);
      padding: 0 !important;
      box-sizing: border-box;
      overflow: hidden;
      pointer-events: none;
    }

    .screen-lobby-host--touch .lobby-responsive__panel-wrapper {
      pointer-events: auto;
      position: absolute;
      inset: 0;
      display: flex;
      align-items: stretch;
      width: 100%;
      height: 100%;
      min-height: 0;
      max-width: none;
      z-index: 80;
      padding: max(12px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right)) max(18px, env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left));
      box-sizing: border-box;
      overflow: hidden;
    }

    .screen-lobby-host--touch .lobby-responsive__inline-select {
      pointer-events: auto;
      position: absolute;
      left: max(10px, env(safe-area-inset-left));
      right: max(10px, env(safe-area-inset-right));
      bottom: max(10px, env(safe-area-inset-bottom));
      width: auto;
      max-width: none;
      z-index: 80;
    }

    .screen-lobby-host--touch .lobby-responsive__panel {
      flex: 1 1 auto;
      max-width: none;
      max-height: 100%;
      height: auto;
      min-height: 0;
      padding: 14px !important;
      overflow-y: auto;
    }

    .screen-lobby-host--touch .lobby-responsive__panel > div {
      min-height: 0;
      height: auto;
    }

    .screen-lobby-host--touch .lobby-responsive__inline-select {
      display: flex;
      height: min(42vh, 390px);
      min-height: 300px;
      align-items: stretch;
      background: transparent;
    }

    .screen-lobby-host--touch .lobby-responsive__inline-select-close {
      display: none;
    }

    .screen-lobby-host--touch .lobby-host__slot-grid {
      max-height: clamp(180px, 28vh, 300px);
      overflow-y: auto;
    }

    .screen-lobby-host--touch .lobby-host__ready-controls {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr);
      gap: 8px;
    }

    .screen-lobby-host--touch .lobby-host__top-actions {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
      align-items: stretch;
    }

    .screen-lobby-host--touch .lobby-host__top-actions > button,
    .screen-lobby-host--touch .lobby-host__top-actions .lobby-host__ready-controls button {
      width: 100%;
    }

    .screen-lobby-host--touch .lobby-host__top-actions > .lobby-host__ready-controls {
      grid-column: 1 / -1;
    }

    .screen-lobby-host--touch .lobby-host__touch-player-actions {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto auto;
      grid-column: 1 / -1;
      align-items: center;
      gap: 8px;
    }

    .screen-lobby-host--touch [data-info-button] {
      width: 44px !important;
      height: 44px !important;
      min-width: 44px !important;
      min-height: 44px !important;
      line-height: 42px !important;
      cursor: pointer;
      touch-action: manipulation;
    }

    .screen-lobby-host--touch-customize .lobby-responsive__panel-wrapper {
      display: none;
    }

    .screen-lobby-host--touch-hidden .lobby-responsive__panel-wrapper {
      display: none;
    }

    .screen-lobby-host--touch-hidden .lobby-host__top-actions {
      display: none !important;
    }

    .lobby-host__room-fab {
      display: none;
      pointer-events: auto;
      position: absolute;
      left: max(12px, env(safe-area-inset-left));
      bottom: max(12px, env(safe-area-inset-bottom));
      z-index: 90;
    }

    .screen-lobby-host--touch-hidden .lobby-host__room-fab {
      display: block;
    }

    .lobby-responsive__panel-wrapper {
      flex: 0 0 clamp(280px, 24vw, 360px);
      max-width: 360px;
      min-width: 0;
      z-index: 10;
    }

    .lobby-responsive__panel {
      width: 100%;
      max-width: ${PANEL_WIDTH};
      max-height: calc(var(--app-viewport-height, 100vh) - (var(--lobby-screen-padding) * 2));
      padding: var(--lobby-panel-padding-y) var(--lobby-panel-padding-x) !important;
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    .lobby-responsive__inline-select {
      display: flex;
      flex: 0 0 clamp(260px, 23vw, 340px);
      min-width: 0;
      height: 100%;
      min-height: 0;
      z-index: 100;
      background: transparent;
      pointer-events: auto;
      align-items: center;
      position: relative;
    }

    .lobby-responsive__inline-select-close {
      position: absolute;
      top: 8px;
      right: 8px;
      z-index: 4;
      width: 48px !important;
      height: 48px !important;
      min-width: 48px !important;
      min-height: 48px !important;
      max-width: 48px !important;
      max-height: 48px !important;
      padding: 0 !important;
    }

    @media (min-width: ${BREAKPOINT_WIDE}) {
      .lobby-responsive__panel-wrapper {
        flex-basis: 360px;
      }
    }

    @media (min-width: ${BREAKPOINT_NARROW}) and (max-width: 1279px) {
      .screen-lobby-host,
      .screen-lobby-joiner {
        --lobby-screen-gap: 16px;
      }

      .lobby-responsive__panel-wrapper {
        flex-basis: clamp(260px, 30vw, 340px);
      }

      .lobby-responsive__inline-select {
        flex-basis: clamp(240px, 30vw, 320px);
      }
    }

    @media (max-width: 719px) {
      .screen-lobby-host,
      .screen-lobby-joiner {
        --lobby-screen-padding: 16px;
        --lobby-screen-gap: 16px;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        overflow-x: hidden;
        overflow-y: auto;
        padding-bottom: max(16px, env(safe-area-inset-bottom));
      }

      .screen-lobby-host--touch {
        overflow: hidden;
        padding: 0 !important;
      }

      .lobby-responsive__panel-wrapper {
        flex: 0 0 auto;
        width: 100%;
        max-width: ${PANEL_WIDTH};
        align-self: center;
      }

      .lobby-responsive__panel {
        max-height: none;
      }

      .screen-lobby-host--touch .lobby-responsive__panel-wrapper {
        flex: none;
        width: 100%;
        height: 100%;
        max-width: none;
        align-self: stretch;
      }

      .screen-lobby-host--touch .lobby-responsive__panel {
        max-height: 100%;
      }

      .lobby-responsive__inline-select {
        flex: 0 0 auto;
        width: 100%;
        height: auto;
        min-height: 0;
        max-width: ${PANEL_WIDTH};
        align-self: center;
        align-items: stretch;
      }
    }

    @media (max-height: 900px) and (min-width: ${BREAKPOINT_NARROW}) {
      .screen-lobby-host,
      .screen-lobby-joiner {
        --lobby-screen-padding: 16px;
        --lobby-screen-gap: 16px;
        --lobby-panel-padding-y: 14px;
        --lobby-panel-padding-x: 18px;
        --lobby-panel-content-gap: 12px;
        --lobby-room-code-font-size: 40px;
        --lobby-slot-min-height: 78px;
        --lobby-slot-padding: 8px 6px;
        --lobby-empty-slot-padding: 6px;
        --lobby-slot-gap: 4px;
        --lobby-avatar-size: 30px;
        --lobby-settings-padding: 8px;
        --lobby-settings-gap: 6px;
        --layout-button-min-height: 38px;
        --layout-button-padding: 8px 14px;
        --typography-size-h5: 16px;
      }
    }

    @media (max-height: 760px) and (min-width: ${BREAKPOINT_NARROW}) {
      .screen-lobby-host,
      .screen-lobby-joiner {
        --lobby-screen-padding: 10px;
        --lobby-screen-gap: 10px;
        --lobby-panel-padding-y: 10px;
        --lobby-panel-padding-x: 14px;
        --lobby-panel-content-gap: 8px;
        --lobby-room-code-font-size: 34px;
        --lobby-slot-min-height: 64px;
        --lobby-slot-padding: 6px;
        --lobby-empty-slot-padding: 5px;
        --lobby-slot-gap: 3px;
        --lobby-avatar-size: 24px;
        --lobby-settings-padding: 6px;
        --lobby-settings-gap: 4px;
        --layout-button-min-height: 34px;
        --layout-button-padding: 6px 10px;
        --typography-size-h5: 14px;
        --typography-size-caption: 12px;
      }
    }
  `;
  document.head.appendChild(styleEl);
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createLobbyHostScreen(ctx: ScreenMountContext<LobbyMountPayload>): ScreenHandle {
  const { gsapRegistry, host, payload, router } = ctx;

  let disposed = false;
  let hostId: string | null = payload.restoredSession?.hostId ?? null;
  let roomCode = payload.restoredSession?.roomCode ?? payload.code ?? '----';
  const players: PlayerEntry[] = [];
  // selfPlayerId is the host's own player id (captured from room_created).
  let selfPlayerId: string | null = payload.restoredSession?.playerId ?? null;
  // Track per-player customization from state_sync so inline char-select has initial values.
  const playerCustomizations = new Map<string, CharacterCustomization>();
  const ownedPlayerIds = new Set<string>(payload.initialOwnedPlayerIds ?? []);
  let currentSettings = structuredClone(DEFAULT_GAME_SETTINGS);
  let roomCreated = payload.skipInitialRequest === true;
  const touchLayout = isTouchLobbyLayout();
  let mobileMode: 'room' | 'customize' | 'hidden' = 'room';
  let customizationPreloadInFlight = false;
  // Track the currently open inline character-select overlay dispose function.
  let inlineSelectDispose: ((opts?: { readonly restoreCamera?: boolean }) => void) | null = null;
  let inlineSelectPlayerId: string | null = null;
  let touchInitialCustomizeRequested = false;

  if (payload.skipInitialRequest === true && payload.restoredSession != null) {
    const restoredPlayerId = payload.restoredSession.playerId;
    const ownsRestoredPlayer =
      ownedPlayerIds.size > 0
        ? ownedPlayerIds.has(restoredPlayerId)
        : restoredPlayerId === payload.restoredSession.hostId;
    if (ownsRestoredPlayer) {
      players.push({
        playerId: restoredPlayerId,
        playerName: payload.playerName,
        isBot: false,
        isLocal: false,
        isReady: false,
      });
      if (payload.preferredCustomization != null) {
        playerCustomizations.set(restoredPlayerId, payload.preferredCustomization);
      }
    }
  }

  // Track button handles for cleanup
  const buttonHandles: UIComponentHandle[] = [];
  const settingsHandles: UIComponentHandle[] = [];
  let backButtonHandle: UIComponentHandle | null = null;

  function navigateBack(): void {
    const target = payload.escBackTarget;
    if (target != null) {
      if (roomCreated) target.beforeNavigate?.();
      router.navigate(target.screenId, target.payload);
      return;
    }
    if (roomCreated) {
      payload.send({ type: 'leave' });
    }
    payload.send(buildWarmupSubscribeMessage({ isReady: hasSeenWarmupTutorial() }));
    router.navigate('warmup-arena-overlay', { portal: null });
  }

  function recoverAfterMaintenanceRejection(): void {
    const target = payload.escBackTarget;
    if (target != null) {
      target.beforeNavigate?.();
      router.navigate(target.screenId, target.payload);
      return;
    }
    if (roomCreated) {
      payload.send({ type: 'leave' });
    }
    payload.send(buildWarmupSubscribeMessage({ isReady: hasSeenWarmupTutorial() }));
    router.navigate('warmup-arena-overlay', { portal: null });
  }

  // ── Root element ─────────────────────────────────────────────────────────

  injectLobbyResponsiveStyles();

  const rootEl = document.createElement('div');
  rootEl.className = touchLayout
    ? 'screen-lobby-host screen-lobby-host--touch screen-lobby-host--touch-room'
    : 'screen-lobby-host';
  rootEl.style.cssText = [
    'position: relative',
    'width: 100%',
    touchLayout ? 'height: var(--app-viewport-height, 100vh)' : 'height: 100%',
  ].join('; ');

  function syncTouchModeClass(): void {
    rootEl.classList.toggle('screen-lobby-host--touch-room', touchLayout && mobileMode === 'room');
    rootEl.classList.toggle(
      'screen-lobby-host--touch-customize',
      touchLayout && mobileMode === 'customize',
    );
    rootEl.classList.toggle(
      'screen-lobby-host--touch-hidden',
      touchLayout && mobileMode === 'hidden',
    );
  }

  // ── Panel wrapper ────────────────────────────────────────────────────────

  const panelWrapper = document.createElement('div');
  panelWrapper.className = 'lobby-responsive__panel-wrapper';
  rootEl.appendChild(panelWrapper);

  const roomFab = document.createElement('div');
  roomFab.className = 'lobby-host__room-fab';
  rootEl.appendChild(roomFab);

  const roomFabHandle = createButton({
    gsapRegistry,
    mount: roomFab,
    variant: 'primary',
    size: 'small',
    label: 'Room',
    onClick: () => {
      if (disposed || !touchLayout) return;
      mobileMode = 'room';
      syncTouchModeClass();
      payload.onCustomizeCamera?.(null);
    },
  });
  roomFabHandle.element.setAttribute('data-action', 'show-room');

  // ── Panel ─────────────────────────────────────────────────────────────────

  const panelHandle: PanelHandle = createPanel({
    gsapRegistry,
    mount: panelWrapper,
    padding: 'lg',
  });

  const panelEl = panelHandle.element;
  panelEl.classList.add('lobby-responsive__panel');
  panelEl.style.width = '100%';
  panelEl.style.boxSizing = 'border-box';

  // Panel content container
  const panelContent = document.createElement('div');
  panelContent.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'gap: var(--lobby-panel-content-gap, 20px)',
  ].join('; ');
  panelEl.appendChild(panelContent);

  const topActionsEl = document.createElement('div');
  topActionsEl.className = 'lobby-host__top-actions';
  topActionsEl.style.cssText = [
    'display: flex',
    'align-items: stretch',
    'gap: 8px',
    'width: 100%',
  ].join('; ');
  panelContent.appendChild(topActionsEl);

  backButtonHandle = createButton({
    gsapRegistry,
    mount: topActionsEl,
    variant: 'ghost',
    size: 'small',
    label: 'BACK',
    onClick: () => {
      if (!disposed) navigateBack();
    },
  });
  backButtonHandle.element.setAttribute('data-action', 'back');

  let hideRoomButtonHandle: UIComponentHandle | null = null;
  if (touchLayout) {
    hideRoomButtonHandle = createButton({
      gsapRegistry,
      mount: topActionsEl,
      variant: 'secondary',
      size: 'small',
      label: 'Hide',
      onClick: () => {
        if (disposed) return;
        mobileMode = 'hidden';
        syncTouchModeClass();
        payload.onCustomizeCamera?.(null);
      },
    });
    hideRoomButtonHandle.element.setAttribute('data-action', 'hide-room');
  }

  // ── Room Code Display ─────────────────────────────────────────────────────

  const codeHandle: RoomCodeDisplayHandle = createRoomCodeDisplay({
    gsapRegistry,
    mount: panelContent,
    code: roomCode,
    subtitle: 'Share this code with friends!',
  });

  // ── Slot grid ────────────────────────────────────────────────────────────

  const centerRow = document.createElement('div');
  centerRow.style.cssText = [
    'display: flex',
    `gap: 16px`,
    'align-items: flex-start',
    'width: 100%',
  ].join('; ');
  panelContent.appendChild(centerRow);

  // ── Slot grid container (left ~60%) ──────────────────────────────────────

  const gridEl = document.createElement('div');
  gridEl.className = 'lobby-host__slot-grid';
  gridEl.style.cssText = [
    'display: flex',
    'flex-wrap: wrap',
    `gap: ${SLOT_GAP}`,
    'width: 100%',
    'flex: 1 1 100%',
  ].join('; ');
  centerRow.appendChild(gridEl);

  const settingsCol = document.createElement('div');
  settingsCol.className = 'lobby-host__settings';
  settingsCol.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'gap: var(--lobby-settings-gap, 8px)',
    'width: 100%',
    'box-sizing: border-box',
    'padding: var(--lobby-settings-padding, 10px)',
    'background: rgba(0,0,0,0.10)',
    'border: 1px solid rgba(255,255,255,0.14)',
    'border-radius: 10px',
  ].join('; ');
  panelContent.appendChild(settingsCol);

  let roundsStepper: ReturnType<typeof createNumberStepper> | null = null;
  let turnTimerStepper: ReturnType<typeof createNumberStepper> | null = null;
  let unlimitedToggle: ReturnType<typeof createSettingsToggle> | null = null;
  let betweenRoundMinigamesToggle: ReturnType<typeof createSettingsToggle> | null = null;
  const modifierToggles = new Map<string, ReturnType<typeof createSettingsToggle>>();

  function settingsLabel(text: string): HTMLElement {
    const el = document.createElement('div');
    el.textContent = text;
    el.style.cssText = [
      `color: ${COLOR_TEXT_SECONDARY}`,
      `font-family: ${FONT_HEADLINE}`,
      'font-size: 12px',
      'letter-spacing: 1px',
    ].join('; ');
    return el;
  }

  function updateToggleDataset(key: string, value: boolean): void {
    const handle =
      key === 'unlimited'
        ? unlimitedToggle
        : key === 'betweenRoundMinigames'
          ? betweenRoundMinigamesToggle
          : modifierToggles.get(key);
    handle?.element.setAttribute('data-value', String(value));
  }

  function styleCompactToggle(element: HTMLElement): void {
    element.style.width = '100%';
    element.style.justifyContent = 'space-between';
    element.style.gap = '8px';
    const label = element.querySelector('.toggle-label') as HTMLElement | null;
    if (label != null) {
      label.style.fontSize = '12px';
      label.style.fontFamily = FONT_HEADLINE;
      label.style.minWidth = '0';
    }
  }

  function attachInfoButton(element: HTMLElement, text: string): void {
    element.style.position = element.style.position || 'relative';
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'i';
    button.title = text;
    button.setAttribute('aria-label', text);
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('data-info-button', '');
    button.style.cssText = [
      'width: 18px',
      'height: 18px',
      'border-radius: 50%',
      'border: 1px solid rgba(255,255,255,0.35)',
      'background: rgba(255,255,255,0.10)',
      `color: ${COLOR_TEXT_PRIMARY}`,
      'font-size: 12px',
      'font-weight: 800',
      'line-height: 16px',
      'padding: 0',
      'cursor: help',
      'flex: 0 0 auto',
    ].join('; ');

    function closePopover(): void {
      element.querySelector('[data-info-popover]')?.remove();
      button.setAttribute('aria-expanded', 'false');
    }

    function openPopover(): void {
      closePopover();
      const popover = document.createElement('div');
      popover.setAttribute('data-info-popover', '');
      popover.textContent = text;
      popover.style.cssText = [
        'position: absolute',
        'right: 0',
        'top: calc(100% + 6px)',
        'z-index: 50',
        'width: min(240px, calc(100vw - 48px))',
        'box-sizing: border-box',
        'padding: 8px 10px',
        'border-radius: 8px',
        'border: 1px solid rgba(255,255,255,0.22)',
        'background: rgba(16,20,30,0.96)',
        `color: ${COLOR_TEXT_PRIMARY}`,
        "font-family: var(--typography-family-body, 'Nunito Sans', 'Nunito', sans-serif)",
        'font-size: 12px',
        'line-height: 1.35',
        'box-shadow: 0 8px 24px rgba(0,0,0,0.35)',
        'pointer-events: auto',
      ].join('; ');
      popover.addEventListener('click', (event) => event.stopPropagation());
      element.appendChild(popover);
      button.setAttribute('aria-expanded', 'true');
    }

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (button.getAttribute('aria-expanded') === 'true') {
        closePopover();
      } else {
        openPopover();
      }
    });
    element.appendChild(button);
  }

  function syncSettingsControls(): void {
    roundsStepper?.setValue(currentSettings.maxRounds);
    turnTimerStepper?.setValue(currentSettings.turnTimer === 0 ? 30 : currentSettings.turnTimer);
    unlimitedToggle?.setValue(currentSettings.turnTimer === 0);
    updateToggleDataset('unlimited', currentSettings.turnTimer === 0);
    const betweenRoundMinigamesEnabled = isBetweenRoundMinigamesEnabled(currentSettings);
    betweenRoundMinigamesToggle?.setValue(betweenRoundMinigamesEnabled);
    updateToggleDataset('betweenRoundMinigames', betweenRoundMinigamesEnabled);
    for (const [key, value] of Object.entries(currentSettings.boardModifiers)) {
      const handle = modifierToggles.get(key);
      handle?.setValue(value);
      updateToggleDataset(key, value);
    }
  }

  function sendBoardModifierPatch(
    key: keyof typeof currentSettings.boardModifiers,
    value: boolean,
  ) {
    currentSettings = {
      ...currentSettings,
      boardModifiers: { ...currentSettings.boardModifiers, [key]: value },
    };
    updateToggleDataset(key, value);
    payload.send({
      type: 'configure',
      settings: { boardModifiers: currentSettings.boardModifiers },
    });
  }

  function sendBetweenRoundMinigamesPatch(value: boolean): void {
    currentSettings = { ...currentSettings, betweenRoundMinigamesEnabled: value };
    updateToggleDataset('betweenRoundMinigames', value);
    payload.send({
      type: 'configure',
      settings: { betweenRoundMinigamesEnabled: value },
    });
  }

  function buildBoardGameSettings(): void {
    settingsCol.replaceChildren();

    const headerRow = document.createElement('div');
    headerRow.style.cssText = [
      'display: flex',
      'align-items: center',
      'justify-content: space-between',
      'gap: 8px',
    ].join('; ');
    settingsCol.appendChild(headerRow);

    const mapName = settingsLabel('SKYFORGE');
    headerRow.appendChild(mapName);

    const diceRow = settingsLabel(
      `Dice: ${currentSettings.minDiceRoll}-${currentSettings.maxDiceRoll}`,
    );
    headerRow.appendChild(diceRow);

    const controlsGrid = document.createElement('div');
    controlsGrid.style.cssText = [
      'display: grid',
      'grid-template-columns: repeat(2, minmax(0, 1fr))',
      'gap: 8px',
      'align-items: start',
    ].join('; ');
    settingsCol.appendChild(controlsGrid);

    function settingBlock(label: string): HTMLElement {
      const block = document.createElement('div');
      block.style.cssText = [
        'display: flex',
        'flex-direction: column',
        'gap: 4px',
        'min-width: 0',
      ].join('; ');
      block.appendChild(settingsLabel(label));
      return block;
    }

    const roundsRow = settingBlock('Rounds');
    roundsStepper = createNumberStepper({
      gsapRegistry,
      mount: roundsRow,
      min: 10,
      max: 30,
      step: 5,
      value: currentSettings.maxRounds,
      onChange: (maxRounds) => {
        currentSettings = { ...currentSettings, maxRounds };
        payload.send({ type: 'configure', settings: { maxRounds } });
      },
    });
    settingsHandles.push(roundsStepper);
    controlsGrid.appendChild(roundsRow);

    const timerRow = settingBlock('Turn Timer');
    turnTimerStepper = createNumberStepper({
      gsapRegistry,
      mount: timerRow,
      min: 5,
      max: 120,
      step: 5,
      value: currentSettings.turnTimer === 0 ? 30 : currentSettings.turnTimer,
      onChange: (turnTimer) => {
        currentSettings = { ...currentSettings, turnTimer };
        unlimitedToggle?.setValue(false);
        updateToggleDataset('unlimited', false);
        payload.send({ type: 'configure', settings: { turnTimer } });
      },
    });
    settingsHandles.push(turnTimerStepper);
    controlsGrid.appendChild(timerRow);

    unlimitedToggle = createSettingsToggle({
      gsapRegistry,
      mount: timerRow,
      label: 'UNLIMITED',
      value: currentSettings.turnTimer === 0,
      onChange: (enabled) => {
        const turnTimer = enabled ? 0 : (turnTimerStepper?.value ?? 30);
        currentSettings = { ...currentSettings, turnTimer };
        updateToggleDataset('unlimited', enabled);
        payload.send({ type: 'configure', settings: { turnTimer } });
      },
    });
    unlimitedToggle.element.setAttribute('data-toggle', 'unlimited');
    styleCompactToggle(unlimitedToggle.element);
    attachInfoButton(unlimitedToggle.element, 'Unlimited: no timed decision deadlines.');
    updateToggleDataset('unlimited', currentSettings.turnTimer === 0);
    settingsHandles.push(unlimitedToggle);

    const fieldset = document.createElement('fieldset');
    fieldset.className = 'lobby-host__special-rules';
    fieldset.style.cssText = [
      'border: 1px solid rgba(255,255,255,0.18)',
      'border-radius: 8px',
      'padding: 8px',
      'display: flex',
      'flex-direction: column',
      'gap: 6px',
    ].join('; ');
    const legend = document.createElement('legend');
    legend.textContent = 'SPECIAL RULES';
    legend.style.cssText = [`color: ${COLOR_TEXT_PRIMARY}`, `font-family: ${FONT_HEADLINE}`].join(
      '; ',
    );
    fieldset.appendChild(legend);
    settingsCol.appendChild(fieldset);

    const modifiers = [
      ['pinata', 'PINATA', 'Pinata: damage key drops drop all keys.'],
      ['doubleTime', 'DOUBLE TIME', 'Double Time: all timed player decisions are halved.'],
      ['oneHitKill', 'ONE HIT KILL', 'One Hit Kill: any damage defeats the player.'],
    ] as const;
    for (const [key, label, info] of modifiers) {
      const handle = createSettingsToggle({
        gsapRegistry,
        mount: fieldset,
        label,
        value: currentSettings.boardModifiers[key],
        onChange: (value) => sendBoardModifierPatch(key, value),
      });
      handle.element.setAttribute('data-toggle', key);
      handle.element.setAttribute('data-value', String(currentSettings.boardModifiers[key]));
      styleCompactToggle(handle.element);
      attachInfoButton(handle.element, info);
      modifierToggles.set(key, handle);
      settingsHandles.push(handle);
    }

    betweenRoundMinigamesToggle = createSettingsToggle({
      gsapRegistry,
      mount: fieldset,
      label: 'BETWEEN-ROUND MINIGAMES',
      value: isBetweenRoundMinigamesEnabled(currentSettings),
      onChange: (value) => sendBetweenRoundMinigamesPatch(value),
    });
    betweenRoundMinigamesToggle.element.setAttribute('data-toggle', 'betweenRoundMinigames');
    betweenRoundMinigamesToggle.element.setAttribute(
      'data-value',
      String(isBetweenRoundMinigamesEnabled(currentSettings)),
    );
    styleCompactToggle(betweenRoundMinigamesToggle.element);
    attachInfoButton(
      betweenRoundMinigamesToggle.element,
      'Between-round minigames: automatic board minigames after each completed round.',
    );
    settingsHandles.push(betweenRoundMinigamesToggle);
  }

  function buildMinigamesOnlySettings(): void {
    settingsCol.replaceChildren();
    const label = settingsLabel('MINIGAMES ONLY MODE');
    label.style.color = COLOR_TEXT_PRIMARY;
    settingsCol.appendChild(label);
  }

  // ── READY controls ───────────────────────────────────────────────────────

  const readyControlsEl = document.createElement('div');
  readyControlsEl.className = 'lobby-host__ready-controls';
  readyControlsEl.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'gap: 8px',
    'align-items: stretch',
  ].join('; ');
  (touchLayout ? topActionsEl : panelContent).appendChild(readyControlsEl);

  // ── Panel bottom: START GAME button (full width) ──────────────────────────

  const panelBottom = document.createElement('div');
  panelBottom.style.cssText = ['display: flex', 'justify-content: center', 'margin-top: auto'].join(
    '; ',
  );
  panelContent.appendChild(panelBottom);

  // START GAME button — created with disabled:false so the onClick closure is
  // always live. Visual disabled state is managed externally via updateStartButton().
  const startHandle = createButton({
    gsapRegistry,
    mount: panelBottom,
    variant: 'primary',
    size: 'large',
    label: 'START GAME',
    disabled: false,
    onClick: () => {
      if (disposed) return;
      // Guard against click when visually disabled (pointer-events:none prevents
      // most paths, but be explicit for keyboard / programmatic invocations).
      if (!canStartGame()) return;
      payload.send({ type: 'start_game' });
    },
  });
  startHandle.element.setAttribute('data-action', 'start-game');
  startHandle.element.style.width = '100%';

  /** Sync START GAME visual disabled state based on current player count. */
  function canStartGame(): boolean {
    return players.length >= 2 && players.every((player) => player.isBot || player.isReady);
  }

  function updateStartButton(): void {
    const canStart = canStartGame();
    if (canStart) {
      startHandle.element.removeAttribute('disabled');
      startHandle.element.removeAttribute('aria-disabled');
      startHandle.element.style.opacity = '1';
      startHandle.element.style.cursor = 'pointer';
      startHandle.element.style.pointerEvents = 'auto';
    } else {
      startHandle.element.setAttribute('disabled', '');
      startHandle.element.setAttribute('aria-disabled', 'true');
      startHandle.element.style.opacity = '0.55';
      startHandle.element.style.cursor = 'not-allowed';
      startHandle.element.style.pointerEvents = 'none';
    }
  }

  // Apply initial disabled state (0 players < 2)
  updateStartButton();

  // ─────────────────────────────────────────────────────────────────────────
  // Inline character-select overlay
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Mount an inline character-select panel as an overlay over the lobby screen.
   * The panel is absolutely positioned beside the lobby panel and remains mounted.
   */
  function mountInlineCharacterSelect(
    playerId: string,
    opts: {
      readonly restoreCameraOnReplace?: boolean;
      readonly focusCameraOnMount?: boolean;
    } = {},
  ): void {
    if (inlineSelectDispose != null) {
      // Close any existing inline select first
      inlineSelectDispose({ restoreCamera: opts.restoreCameraOnReplace !== false });
      inlineSelectDispose = null;
      inlineSelectPlayerId = null;
    }
    if (payload.characterAdapter == null) return;
    if (payload.areCharacterCosmeticsLoaded != null && !payload.areCharacterCosmeticsLoaded()) {
      return;
    }

    const player = players.find((p) => p.playerId === playerId);

    const overlayEl = document.createElement('div');
    overlayEl.className = 'lobby-responsive__inline-select';
    rootEl.appendChild(overlayEl);

    const closeHandle = createButton({
      gsapRegistry,
      variant: 'ghost',
      size: 'icon',
      label: '×',
      onClick: closeCustomization,
    });
    closeHandle.element.classList.add('lobby-responsive__inline-select-close');
    closeHandle.element.setAttribute('aria-label', 'Close character customization');
    overlayEl.appendChild(closeHandle.element);

    let doneHandle: UIComponentHandle | null = null;
    if (touchLayout) {
      doneHandle = createButton({
        gsapRegistry,
        variant: 'primary',
        size: 'small',
        label: 'Done',
        onClick: closeCustomization,
      });
      doneHandle.element.setAttribute('data-action', 'customize-done');
      doneHandle.element.style.cssText = [
        doneHandle.element.style.cssText,
        'position: absolute',
        'top: 8px',
        'right: 8px',
        'z-index: 6',
      ].join('; ');
      overlayEl.appendChild(doneHandle.element);
    }

    // Resolve taken colors — all colors EXCEPT this player's own
    const takenColors: ColorId[] = players
      .filter((p) => p.playerId !== playerId)
      .map((p) => {
        const c = playerCustomizations.get(p.playerId);
        return c?.color ?? null;
      })
      .filter((c): c is ColorId => c != null);

    // Resolve initial customization for this player
    const initial = playerCustomizations.get(playerId) ?? {
      color: 'red',
      head: 'horse' as const,
      eyes: 'dots' as const,
      cape: 'none' as const,
    };

    const selectHandle = createCharacterSelectScreen({
      gsapRegistry,
      host: overlayEl,
      router,
      payload: {
        adapter: payload.characterAdapter,
        takenColors,
        initial,
        ready: player?.isReady ?? false,
        playerName: player?.playerName,
        roomCode,
        mode: 'inline',
        layoutMode: touchLayout ? 'mobileCategories' : 'desktopRail',
        onCustomizeCamera: (view) => payload.onCustomizeCamera?.({ playerId, view }),
        onSelect: (c: CharacterCustomization) => {
          playerCustomizations.set(playerId, c);
          renderSlots();
          payload.send({ type: 'set_character_customization', playerId, customization: c });
        },
        onReady: (c: CharacterCustomization, isReady: boolean) => {
          payload.send({ type: 'set_character_customization', playerId, customization: c });
          payload.send({ type: 'set_ready', playerId, isReady });
        },
      },
    });
    // Re-enable pointer events on the character-select panel so clicks register
    // (overlay container is pointer-events:none to let the 3D lobby show through)
    selectHandle.element.style.pointerEvents = 'auto';
    mobileMode = touchLayout ? 'customize' : mobileMode;
    syncTouchModeClass();
    if (opts.focusCameraOnMount === true) {
      payload.onCustomizeCamera?.({ playerId, view: 'front' });
    }

    inlineSelectDispose = (disposeOpts = {}) => {
      closeHandle.dispose();
      doneHandle?.dispose();
      selectHandle.dispose();
      if (overlayEl.parentNode) {
        overlayEl.parentNode.removeChild(overlayEl);
      }
      if (disposeOpts.restoreCamera !== false) {
        payload.onCustomizeCamera?.(null);
      }
    };
    inlineSelectPlayerId = playerId;
  }

  function isOwnedHumanPlayer(player: PlayerEntry): boolean {
    if (player.isBot) return false;
    if (ownedPlayerIds.size > 0) return ownedPlayerIds.has(player.playerId);
    return player.playerId === selfPlayerId;
  }

  function syncInlineCharacterSelect(
    preferredPlayerId?: string,
    opts?: { remount?: boolean; silentCamera?: boolean },
  ): void {
    if (payload.characterAdapter == null || selfPlayerId == null) return;
    const ownedPlayers = players.filter(isOwnedHumanPlayer);
    if (ownedPlayers.length === 0) return;

    const preferred = preferredPlayerId
      ? ownedPlayers.find((player) => player.playerId === preferredPlayerId)
      : undefined;
    const active = inlineSelectPlayerId
      ? ownedPlayers.find((player) => player.playerId === inlineSelectPlayerId)
      : undefined;
    const next =
      preferred ??
      (active && !active.isReady ? active : undefined) ??
      ownedPlayers.find((player) => !player.isReady) ??
      active ??
      ownedPlayers[0]!;

    if (opts?.remount === true || next.playerId !== inlineSelectPlayerId) {
      mountInlineCharacterSelect(next.playerId, {
        restoreCameraOnReplace: opts?.silentCamera !== true,
        focusCameraOnMount: touchLayout,
      });
    }
  }

  async function openDesktopInlineCharacterSelect(
    preferredPlayerId?: string,
    opts?: { remount?: boolean; silentCamera?: boolean },
  ): Promise<void> {
    if (disposed || touchLayout) return;
    if (payload.characterAdapter == null || selfPlayerId == null) return;
    if (payload.areCharacterCosmeticsLoaded != null && !payload.areCharacterCosmeticsLoaded()) {
      if (payload.ensureCharacterCosmeticsLoaded == null || customizationPreloadInFlight) return;
      customizationPreloadInFlight = true;
      renderReadyControls();
      try {
        await payload.ensureCharacterCosmeticsLoaded();
      } finally {
        customizationPreloadInFlight = false;
        if (!disposed) renderReadyControls();
      }
      if (disposed || payload.areCharacterCosmeticsLoaded?.() === false) return;
    }
    syncInlineCharacterSelect(preferredPlayerId, opts);
  }

  function maybeOpenTouchInitialCustomization(): void {
    if (!touchLayout || touchInitialCustomizeRequested || inlineSelectPlayerId != null) return;
    if (payload.characterAdapter == null) return;
    const player = players.find(isOwnedHumanPlayer);
    if (player == null) return;
    touchInitialCustomizeRequested = true;
    void openCustomizationFor(player.playerId);
  }

  async function openCustomizationFor(playerId: string): Promise<void> {
    if (disposed || customizationPreloadInFlight) return;
    if (payload.characterAdapter == null) return;
    if (payload.areCharacterCosmeticsLoaded != null && !payload.areCharacterCosmeticsLoaded()) {
      if (payload.ensureCharacterCosmeticsLoaded == null) return;
      customizationPreloadInFlight = true;
      renderReadyControls();
      try {
        await payload.ensureCharacterCosmeticsLoaded();
      } finally {
        customizationPreloadInFlight = false;
        if (!disposed) renderReadyControls();
      }
      if (disposed || !payload.areCharacterCosmeticsLoaded()) return;
    }
    mountInlineCharacterSelect(playerId, { focusCameraOnMount: touchLayout });
  }

  function closeCustomization(): void {
    inlineSelectDispose?.({ restoreCamera: false });
    inlineSelectDispose = null;
    inlineSelectPlayerId = null;
    if (touchLayout) {
      mobileMode = 'room';
      syncTouchModeClass();
    }
    payload.onCustomizeCamera?.(null);
  }

  function renderReadyControls(): void {
    while (readyControlsEl.firstChild) {
      readyControlsEl.removeChild(readyControlsEl.firstChild);
    }

    const ownedPlayers = players.filter(isOwnedHumanPlayer);
    for (const player of ownedPlayers) {
      const rowEl = document.createElement('div');
      rowEl.className = touchLayout ? 'lobby-host__touch-player-actions' : '';
      rowEl.style.cssText = [
        touchLayout ? '' : 'display: grid',
        `grid-template-columns: ${touchLayout ? 'minmax(0, 1fr) auto auto' : 'minmax(0, 1fr) auto'}`,
        'align-items: center',
        'gap: 8px',
        'min-width: 0',
      ]
        .filter(Boolean)
        .join('; ');
      readyControlsEl.appendChild(rowEl);

      const nameEl = document.createElement('div');
      nameEl.textContent = player.playerName;
      nameEl.style.cssText = [
        `color: ${COLOR_TEXT_PRIMARY}`,
        `font-family: ${FONT_HEADLINE}`,
        'font-size: 13px',
        'font-weight: 700',
        'overflow: hidden',
        'text-overflow: ellipsis',
        'white-space: nowrap',
      ].join('; ');
      rowEl.appendChild(nameEl);

      if (touchLayout) {
        const customizeLoaded =
          payload.areCharacterCosmeticsLoaded == null || payload.areCharacterCosmeticsLoaded();
        const customizeHandle = createButton({
          gsapRegistry,
          mount: rowEl,
          variant: 'secondary',
          size: 'small',
          label: customizationPreloadInFlight && !customizeLoaded ? 'Loading...' : 'Customize',
          disabled:
            payload.characterAdapter == null ||
            (payload.ensureCharacterCosmeticsLoaded == null && !customizeLoaded) ||
            customizationPreloadInFlight,
          onClick: () => {
            void openCustomizationFor(player.playerId);
          },
        });
        customizeHandle.element.setAttribute('data-action', 'customize-player');
        customizeHandle.element.setAttribute('data-player-id', player.playerId);
        buttonHandles.push(customizeHandle);
      }

      const readyHandle = createButton({
        gsapRegistry,
        mount: rowEl,
        variant: player.isReady ? 'secondary' : 'primary',
        size: 'small',
        label: player.isReady ? 'UNREADY' : 'READY',
        onClick: () => {
          if (disposed) return;
          const customization = playerCustomizations.get(player.playerId);
          if (customization != null) {
            payload.send({
              type: 'set_character_customization',
              playerId: player.playerId,
              customization,
            });
          }
          payload.send({ type: 'set_ready', playerId: player.playerId, isReady: !player.isReady });
        },
      });
      readyHandle.element.setAttribute('data-action', 'ready');
      readyHandle.element.setAttribute('data-player-id', player.playerId);
      buttonHandles.push(readyHandle);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Slot rendering
  // ─────────────────────────────────────────────────────────────────────────

  /** Dispose all tracked button handles and clear the array. */
  function disposeButtonHandles(): void {
    for (const h of buttonHandles) {
      h.dispose();
    }
    buttonHandles.length = 0;
  }

  /** Build one filled player slot cell. */
  function buildFilledSlot(player: PlayerEntry, index: number): HTMLElement {
    const slotEl = document.createElement('div');
    slotEl.setAttribute('data-player-slot', player.playerId);
    slotEl.style.cssText = [
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'justify-content: center',
      'gap: var(--lobby-slot-gap, 6px)',
      'width: calc(50% - 4px)',
      'min-height: var(--lobby-slot-min-height, 100px)',
      'box-sizing: border-box',
      'padding: var(--lobby-slot-padding, 12px 8px)',
      `background: rgba(0,0,0,0.20)`,
      `border-radius: 10px`,
      'position: relative',
    ].join('; ');

    // Colored circle avatar
    const avatarEl = document.createElement('div');
    const customization = playerCustomizations.get(player.playerId);
    const resolvedPlayerColor =
      customization != null
        ? `#${COLOR_ID_TO_HEX[customization.color].toString(16).padStart(6, '0')}`
        : (PLAYER_COLORS[index % PLAYER_COLORS.length] ?? '#FFFFFF');
    avatarEl.style.cssText = [
      'width: var(--lobby-avatar-size, 40px)',
      'height: var(--lobby-avatar-size, 40px)',
      `background: ${resolvedPlayerColor}`,
      'border-radius: 50%',
      'flex-shrink: 0',
    ].join('; ');
    slotEl.appendChild(avatarEl);

    if (player.playerId !== hostId) {
      const kickBtn = document.createElement('button');
      kickBtn.type = 'button';
      kickBtn.textContent = '×';
      kickBtn.setAttribute('data-action', 'remove-player');
      kickBtn.setAttribute('aria-label', `Remove ${player.playerName}`);
      kickBtn.style.cssText = [
        'position: absolute',
        'top: 6px',
        'right: 6px',
        'width: 52px',
        'height: 52px',
        'min-width: 52px',
        'min-height: 52px',
        'box-sizing: border-box',
        'border-radius: 50%',
        'border: 1px solid rgba(255,255,255,0.24)',
        'background: rgba(0,0,0,0.35)',
        `color: ${COLOR_TEXT_PRIMARY}`,
        'font-size: 18px',
        'line-height: 1',
        'cursor: pointer',
        'padding: 0',
        'touch-action: manipulation',
      ].join('; ');
      kickBtn.addEventListener('click', () => {
        if (disposed) return;
        payload.send({ type: 'remove_player', playerId: player.playerId });
      });
      slotEl.appendChild(kickBtn);
    }

    // Player name
    const nameEl = document.createElement('div');
    nameEl.textContent = player.playerName;
    nameEl.style.cssText = [
      `color: ${COLOR_TEXT_PRIMARY}`,
      "font-family: var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)",
      'font-size: 14px',
      'font-weight: 700',
      'text-align: center',
      'max-width: 100%',
      'overflow: hidden',
      'text-overflow: ellipsis',
      'white-space: nowrap',
    ].join('; ');
    slotEl.appendChild(nameEl);

    if (player.isReady || player.isBot) {
      const readyPill = document.createElement('div');
      readyPill.textContent = 'READY ✓';
      readyPill.setAttribute('data-ready-badge', player.playerId);
      readyPill.style.cssText = [
        `background: ${COLOR_SUCCESS}`,
        'color: #FFFFFF',
        'font-size: 10px',
        'font-weight: 700',
        'letter-spacing: 1px',
        `padding: 2px 8px`,
        `border-radius: ${BORDER_RADIUS_PILL}`,
        'line-height: 1.4',
      ].join('; ');
      slotEl.appendChild(readyPill);
    }

    if (customization != null) {
      const summaryEl = document.createElement('div');
      summaryEl.textContent = `${formatCustomizationLabel(customization.head)} / ${formatCustomizationLabel(customization.eyes)} / ${formatCustomizationLabel(customization.cape)}`;
      summaryEl.style.cssText = [
        `color: ${COLOR_TEXT_SECONDARY}`,
        'font-size: 10px',
        'text-align: center',
        'max-width: 100%',
        'overflow: hidden',
        'text-overflow: ellipsis',
        'white-space: nowrap',
      ].join('; ');
      slotEl.appendChild(summaryEl);
    }

    // HOST pill (shown only if this player's ID matches hostId)
    if (player.playerId === hostId) {
      const pillEl = document.createElement('div');
      pillEl.textContent = 'HOST';
      pillEl.setAttribute('data-host-pill', '');
      pillEl.style.cssText = [
        `background: ${COLOR_AMBER}`,
        'color: #FFFFFF',
        'font-size: 11px',
        'font-weight: 700',
        'letter-spacing: 1px',
        `padding: 2px 8px`,
        `border-radius: ${BORDER_RADIUS_PILL}`,
        'line-height: 1.4',
      ].join('; ');
      slotEl.appendChild(pillEl);
    }

    return slotEl;
  }

  /** Build one empty slot cell with Add Bot + Add Local Player buttons. */
  function buildEmptySlot(): HTMLElement {
    const slotEl = document.createElement('div');
    slotEl.className = 'lobby-host__empty-slot';
    slotEl.style.cssText = [
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'justify-content: center',
      'gap: var(--lobby-slot-gap, 6px)',
      'width: calc(50% - 4px)',
      'min-height: var(--lobby-slot-min-height, 100px)',
      'box-sizing: border-box',
      'padding: var(--lobby-empty-slot-padding, 8px)',
      `background: rgba(0,0,0,0.10)`,
      `border-radius: 10px`,
      'border: 1px dashed rgba(255,255,255,0.15)',
    ].join('; ');

    // Add Bot button
    const addBotHandle = createButton({
      gsapRegistry,
      mount: slotEl,
      label: 'Add Bot',
      variant: 'ghost',
      size: 'small',
      onClick: () => {
        if (disposed) return;
        payload.send({ type: 'add_bot', difficulty: 'hard' });
      },
    });
    // Mark with data-action for test selection
    addBotHandle.element.setAttribute('data-action', 'add-bot');
    buttonHandles.push(addBotHandle);

    if (!touchLayout) {
      const addLocalHandle = createButton({
        gsapRegistry,
        mount: slotEl,
        label: 'Add Local Player',
        variant: 'ghost',
        size: 'small',
        onClick: () => {
          if (disposed || touchLayout) return;
          const localSlot = 2 + players.filter((player) => player.isLocal).length;
          payload.send({
            type: 'add_local_player',
            playerName: `Player ${localSlot}`,
            localSlot,
          });
        },
      });
      addLocalHandle.element.setAttribute('data-action', 'add-local-player');
      buttonHandles.push(addLocalHandle);
    }

    return slotEl;
  }

  /** Re-render all 8 slot cells from current player state. */
  function renderSlots(): void {
    // Remove existing children from grid
    while (gridEl.firstChild) {
      gridEl.removeChild(gridEl.firstChild);
    }
    // Dispose existing button handles before rebuilding
    disposeButtonHandles();

    const occupiedCount = players.length;

    // Build filled slots for each player
    for (let i = 0; i < occupiedCount; i++) {
      const player = players[i]!;
      const slotEl = buildFilledSlot(player, i);
      gridEl.appendChild(slotEl);
    }

    // Build empty slots for remaining capacity
    for (let i = occupiedCount; i < MAX_PLAYERS; i++) {
      const slotEl = buildEmptySlot();
      gridEl.appendChild(slotEl);
    }

    renderReadyControls();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Server message handler
  // ─────────────────────────────────────────────────────────────────────────

  const unsubscribe = payload.subscribe((msg: ServerMessage) => {
    if (disposed) return;

    if (msg.type === 'room_created') {
      roomCreated = true;
      codeHandle.setCode(msg.code);
      roomCode = msg.code;
      hostId = msg.hostId;
      // Capture the host's own player ID so the always-on panel targets this client.
      selfPlayerId = msg.hostId;
      ownedPlayerIds.clear();
      for (const playerId of msg.ownedPlayerIds) ownedPlayerIds.add(playerId);
      // Re-render slots so any already-joined player gets HOST pill
      renderSlots();
      return;
    }

    if (msg.type === 'notification' && msg.text === 'maintenance_update_pending') {
      recoverAfterMaintenanceRejection();
      return;
    }

    if (msg.type === 'notification' && msg.text === 'color_taken') {
      closeCustomization();
      return;
    }

    if (msg.type === 'player_joined') {
      players.push({
        playerId: msg.playerId,
        playerName: msg.playerName,
        isBot: msg.isBot,
        isLocal: msg.isLocal,
        isReady: msg.isBot,
      });
      renderSlots();
      updateStartButton();
      maybeOpenTouchInitialCustomization();
      if (!touchLayout && (msg.playerId === selfPlayerId || ownedPlayerIds.has(msg.playerId))) {
        void openDesktopInlineCharacterSelect(msg.playerId);
      }
      return;
    }

    if (msg.type === 'player_left') {
      const idx = players.findIndex((p) => p.playerId === msg.playerId);
      if (idx !== -1) {
        players.splice(idx, 1);
        renderSlots();
        updateStartButton();
        if (inlineSelectPlayerId === msg.playerId) {
          closeCustomization();
          void openDesktopInlineCharacterSelect();
        }
      }
      return;
    }

    if (msg.type === 'owned_players_changed') {
      ownedPlayerIds.clear();
      for (const playerId of msg.ownedPlayerIds) ownedPlayerIds.add(playerId);
      renderSlots();
      maybeOpenTouchInitialCustomization();
      if (!touchLayout) {
        void openDesktopInlineCharacterSelect(undefined, {
          remount: inlineSelectPlayerId != null,
          silentCamera: true,
        });
      }
      return;
    }

    if (msg.type === 'settings_changed') {
      currentSettings = structuredClone(msg.settings);
      syncSettingsControls();
      return;
    }

    // game_started arrives just before state_sync (server broadcasts both back-to-back).
    // Set a flag and wait for the state_sync that follows to mount board-hud with a
    // complete payload.
    if (msg.type === 'game_started') {
      gameStartPending = true;
      return;
    }

    if (msg.type === 'state_sync') {
      const state = deserializeGameState(msg.state);
      currentSettings = structuredClone(state.settings);
      syncSettingsControls();
      // State sync is the authoritative lobby snapshot for readiness/customization.
      players.length = 0;
      playerCustomizations.clear();
      for (const id of state.turnOrder) {
        const player = state.players.get(id);
        if (!player) continue;
        players.push({
          playerId: id,
          playerName: player.name,
          isBot: player.isBot,
          isLocal: player.isLocal,
          isReady: player.isBot || player.isReady === true,
        });
      }
      for (const [id, player] of state.players.entries()) {
        if (player.customization != null) {
          playerCustomizations.set(id, player.customization);
        }
      }
      renderSlots();
      updateStartButton();
      if (
        inlineSelectPlayerId != null &&
        !players.some((p) => p.playerId === inlineSelectPlayerId)
      ) {
        closeCustomization();
      }
      maybeOpenTouchInitialCustomization();
      if (!touchLayout) {
        void openDesktopInlineCharacterSelect(undefined, {
          remount: inlineSelectPlayerId != null,
          silentCamera: true,
        });
      }
      if (gameStartPending) {
        gameStartPending = false;
        if (hostId == null) return; // defensive: should never happen post room_created
        router.navigate('board-hud', {
          state,
          localPlayerId: hostId,
          send: payload.send,
          subscribe: payload.subscribe,
        });
        return;
      }
      return;
    }
  });

  let gameStartPending = false;

  // ─────────────────────────────────────────────────────────────────────────
  // Mount
  // ─────────────────────────────────────────────────────────────────────────

  // Initial render of empty slots
  if (payload.mode === 'minigames_only') {
    buildMinigamesOnlySettings();
  } else {
    buildBoardGameSettings();
  }
  renderSlots();

  // Append root to host and trigger panel entrance animation
  host.appendChild(rootEl);
  panelHandle.show();
  if (touchLayout) {
    panelEl.style.opacity = '1';
    panelEl.style.transform = 'none';
  }
  maybeOpenTouchInitialCustomization();
  if (!touchLayout) {
    void openDesktopInlineCharacterSelect(undefined, { silentCamera: true });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Send create_room immediately on mount
  // ─────────────────────────────────────────────────────────────────────────

  if (payload.skipInitialRequest !== true) {
    payload.send({
      type: 'create_room',
      playerName: payload.playerName,
      mode: payload.mode,
      roomType: 'create_game',
      ...(payload.preferredCustomization === undefined
        ? {}
        : { customization: payload.preferredCustomization }),
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // dispose
  // ─────────────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Close any open inline character-select overlay
    if (inlineSelectDispose != null) {
      inlineSelectDispose({ restoreCamera: false });
      inlineSelectDispose = null;
    }
    payload.onCustomizeCamera?.(null);

    unsubscribe();
    disposeButtonHandles();
    for (const handle of settingsHandles) {
      handle.dispose();
    }
    settingsHandles.length = 0;
    startHandle.dispose();
    backButtonHandle?.dispose();
    hideRoomButtonHandle?.dispose();
    roomFabHandle.dispose();
    codeHandle.dispose();
    panelHandle.dispose();

    if (rootEl.parentNode) {
      rootEl.parentNode.removeChild(rootEl);
    }
  }

  return {
    element: rootEl,
    dispose,
  };
}

function formatCustomizationLabel(value: string): string {
  if (value === 'none') return 'No Cape';
  return value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
